import { Box, Button, Drawer, Stack, IconButton, Tooltip, Divider, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close as CloseIcon, ChevronLeft as ChevronLeftIcon, Menu as MenuIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import type { FC } from 'react';
import { useMemo } from 'react';
import { SidebarLogo } from 'src/components/sidebar-logo';
import { RouterLink } from 'src/components/router-link';
import { Scrollbar } from 'src/components/scrollbar';
import { usePathname } from 'src/hooks/use-pathname';
import { paths } from 'src/paths';
import { customerSidebarColors } from 'src/theme/custom/customer-colors';
import type { NavColor } from 'src/types/settings';
import type { Section } from '../config';
import { TenantSwitch } from '../tenant-switch';
import { SideNavSection } from './side-nav-section';

const SIDE_NAV_WIDTH: number = 280;

const useCssVars = (color: NavColor): Record<string, string> => {
  const theme = useTheme();

  return useMemo((): Record<string, string> => {
    switch (color) {
      case 'blend-in':
        if (theme.palette.mode === 'dark') {
          return {
            '--nav-bg': theme.palette.background.default,
            '--nav-color': theme.palette.neutral[100],
            '--nav-border-color': theme.palette.neutral[700],
            '--nav-logo-border': theme.palette.neutral[700],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.neutral[400],
            '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-color': theme.palette.text.primary,
            '--nav-item-disabled-color': theme.palette.neutral[600],
            '--nav-item-icon-color': theme.palette.neutral[500],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[700],
            '--nav-item-chevron-color': theme.palette.neutral[700],
            '--nav-scrollbar-color': theme.palette.neutral[400],
          };
        } else {
          return {
            '--nav-bg': theme.palette.background.default,
            '--nav-color': theme.palette.text.primary,
            '--nav-border-color': theme.palette.neutral[100],
            '--nav-logo-border': theme.palette.neutral[100],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.text.secondary,
            '--nav-item-hover-bg': theme.palette.action.hover,
            '--nav-item-active-bg': theme.palette.action.selected,
            '--nav-item-active-color': theme.palette.text.primary,
            '--nav-item-disabled-color': theme.palette.neutral[400],
            '--nav-item-icon-color': theme.palette.neutral[400],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[400],
            '--nav-item-chevron-color': theme.palette.neutral[400],
            '--nav-scrollbar-color': theme.palette.neutral[900],
          };
        }

      case 'discreet':
        if (theme.palette.mode === 'dark') {
          return {
            '--nav-bg': theme.palette.neutral[900],
            '--nav-color': theme.palette.neutral[100],
            '--nav-border-color': theme.palette.neutral[700],
            '--nav-logo-border': theme.palette.neutral[700],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.neutral[400],
            '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-color': theme.palette.text.primary,
            '--nav-item-disabled-color': theme.palette.neutral[600],
            '--nav-item-icon-color': theme.palette.neutral[500],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[700],
            '--nav-item-chevron-color': theme.palette.neutral[700],
            '--nav-scrollbar-color': theme.palette.neutral[400],
          };
        } else {
          return {
            '--nav-bg': theme.palette.neutral[50],
            '--nav-color': theme.palette.text.primary,
            '--nav-border-color': theme.palette.divider,
            '--nav-logo-border': theme.palette.neutral[200],
            '--nav-section-title-color': theme.palette.neutral[500],
            '--nav-item-color': theme.palette.neutral[500],
            '--nav-item-hover-bg': theme.palette.action.hover,
            '--nav-item-active-bg': theme.palette.action.selected,
            '--nav-item-active-color': theme.palette.text.primary,
            '--nav-item-disabled-color': theme.palette.neutral[400],
            '--nav-item-icon-color': theme.palette.neutral[400],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[400],
            '--nav-item-chevron-color': theme.palette.neutral[400],
            '--nav-scrollbar-color': theme.palette.neutral[900],
          };
        }

      case 'evident':
        if (theme.palette.mode === 'dark') {
          return {
            '--nav-bg': theme.palette.neutral[800],
            '--nav-color': theme.palette.common.white,
            '--nav-border-color': 'transparent',
            '--nav-logo-border': theme.palette.neutral[700],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.neutral[400],
            '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-color': theme.palette.common.white,
            '--nav-item-disabled-color': theme.palette.neutral[500],
            '--nav-item-icon-color': theme.palette.neutral[400],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[500],
            '--nav-item-chevron-color': theme.palette.neutral[600],
            '--nav-scrollbar-color': theme.palette.neutral[400],
          };
        } else {
          return {
            '--nav-bg': theme.palette.neutral[800],
            '--nav-color': theme.palette.common.white,
            '--nav-border-color': 'transparent',
            '--nav-logo-border': theme.palette.neutral[700],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.neutral[400],
            '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-color': theme.palette.common.white,
            '--nav-item-disabled-color': theme.palette.neutral[500],
            '--nav-item-icon-color': theme.palette.neutral[400],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[500],
            '--nav-item-chevron-color': theme.palette.neutral[600],
            '--nav-scrollbar-color': theme.palette.neutral[400],
          };
        }
      case 'uic':
        return customerSidebarColors('uic', theme);

      case 'umea':
        return customerSidebarColors('umea', theme);
      default:
        return {};
    }
  }, [theme, color]);
};

interface SideNavProps {
  color?: NavColor;
  sections?: Section[];
  open: boolean;
  onClose: () => void;
}

export const SideNav: FC<SideNavProps> = (props) => {
  const { color = 'evident', sections = [], open, onClose } = props;
  const pathname = usePathname();
  const cssVars = useCssVars(color);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sideBarWidth = open ? SIDE_NAV_WIDTH : 0;
  return (
    <Drawer
      anchor="left"
      open={open}
      PaperProps={{
        sx: {
          ...cssVars,
          backgroundColor: 'var(--nav-bg)',
          borderRightColor: 'var(--nav-border-color)',
          borderRightStyle: 'solid',
          borderRightWidth: 1,
          color: 'var(--nav-color)',
          width: sideBarWidth,
          transition: 'width 0.3s ease',
          boxShadow: open ? '0 4px 20px rgba(0,0,0,0.1)' : 'none',
        },
      }}
      variant="permanent"
    >
      <Scrollbar
        sx={{
          height: '100%',
          '& .simplebar-content': {
            height: '100%',
          },
          '& .simplebar-scrollbar:before': {
            background: 'var(--nav-scrollbar-color)',
          },
        }}
      >
        {process.env.REACT_APP_LOGO_URL && (
          <Stack
            justifyContent={'center'}
            alignItems={'center'}
            style={{
              backgroundColor: '#fff',
            }}
          >
            <Box
              component={'img'}
              sx={{
                height: '100%',
                width: '100%',
                maxHeight: 64,
                objectFit: 'contain',
                objectPosition: 'center',
                minWidth: '100',
                borderRadius: '0px', // Adds a border radius for rounded corners
                boxShadow: 3, // Adds a shadow effect
              }}
              alt="Example Image"
              src={process.env.REACT_APP_LOGO_URL}
            ></Box>
          </Stack>
        )}
        <Stack sx={{ height: '100%' }}>
          {/* Header with Logo and Close Button */}
          <Stack sx={{ p: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={3} sx={{ flex: 1 }}>
                <Box
                  component={RouterLink}
                  href={paths.index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 48,
                    width: 48,
                    borderRadius: 3,
                    backgroundColor: 'var(--nav-item-hover-bg)',
                    border: '2px solid var(--nav-logo-border)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      borderColor: 'var(--nav-item-icon-active-color)',
                      backgroundColor: 'var(--nav-item-active-bg)',
                      transform: 'scale(1.08)',
                      boxShadow: '0 8px 25px rgba(33, 150, 243, 0.2)',
                      '&::before': {
                        opacity: 1,
                      },
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(66, 165, 245, 0.1) 100%)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    },
                    '& > *': {
                      position: 'relative',
                      zIndex: 1,
                    },
                  }}
                >
                  <SidebarLogo />
                </Box>
                <TenantSwitch sx={{ flexGrow: 1 }} />
              </Stack>
            </Stack>

            <Divider sx={{ borderColor: 'var(--nav-border-color)' }} />
          </Stack>
          {/* Navigation Content */}
          <Stack
            component="nav"
            spacing={2}
            sx={{
              flexGrow: 1,
              px: 2,
              pb: 2,
            }}
          >
            {sections.map((section, index) => (
              <SideNavSection items={section.items} key={index} pathname={pathname} subheader={section.subheader} />
            ))}
          </Stack>

          {/* Footer with subtle branding or additional actions */}
          <Box sx={{ p: 3, pt: 1 }}>
            <Stack direction="row" justifyContent="flex-end">
              <Tooltip title={isMobile ? 'Close navigation' : 'Collapse sidebar'} placement="top">
                <IconButton
                  onClick={onClose}
                  size="small"
                  sx={{
                    color: 'var(--nav-item-icon-color)',
                    backgroundColor: 'var(--nav-item-hover-bg)',
                    border: '1px solid var(--nav-border-color)',
                    borderRadius: 2.5,
                    width: 36,
                    height: 36,
                    transition: 'all 0.3s ease',
                    mb: 2,
                    '&:hover': {
                      backgroundColor: 'var(--nav-item-active-bg)',
                      color: 'var(--nav-item-icon-active-color)',
                      transform: 'scale(1.15) rotate(-5deg)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                      borderColor: 'var(--nav-item-icon-active-color)',
                    },
                  }}
                  aria-label={isMobile ? 'Close navigation' : 'Collapse sidebar'}
                >
                  {isMobile ? <CloseIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Stack>
            <Divider sx={{ borderColor: 'var(--nav-border-color)', mb: 2 }} />
            {/* Future: Add help section or additional footer content here */}
          </Box>
          {/* {
            process.env.REACT_APP_LOGO_URL && (
              <Stack
                justifyContent={'center'}
                alignItems={'center'}
                style={{
                  backgroundColor: '#fff',
                }}
              >
                <Box
                  component={'img'}
                  sx={{
                    height: "100%",
                    width: "100%",
                    maxHeight: 100,
                    objectFit: "contain",
                    objectPosition: "center",
                    minWidth: '100',
                    borderRadius: '0px', // Adds a border radius for rounded corners
                    boxShadow: 3, // Adds a shadow effect
                  }}
                  alt="Example Image"
                  src={process.env.REACT_APP_LOGO_URL}
                >
                </Box>
              </Stack>

            )
          } */}
        </Stack>
      </Scrollbar>
    </Drawer>
  );
};

SideNav.propTypes = {
  color: PropTypes.oneOf<NavColor>(['blend-in', 'discreet', 'evident', 'uic', 'umea']),
  sections: PropTypes.array,
};
